<template>
  <div>
    <AlertsAnimated :alerts="localAlerts" />
    <v-col>
      <v-row justify="space-between" align="center">
        <v-toolbar-title class="headline pl-2">{{
          "Manage Tasks"
        }}</v-toolbar-title>
        <DialogViewTask
          v-if="isOpenViewTask && !isOpenPreInstallModal"
          v-model="isOpenViewTask"
          :task-id="selectedTaskId"
          @task-updated="handleTaskUpdate"
        />
        <DialogPreInstallModal
          v-if="isOpenViewTask && isOpenPreInstallModal"
          v-model="isOpenViewTask"
          :task="selectedTask"
          @task-updated="handleTaskUpdate"
        />
        <DialogTaskRule
          v-model="showTaskDialog"
          :is-editing="false"
          @task-saved="handleTaskSaved"
          @show-alert="handleShowAlert"
        />
        <v-col cols="2" class="d-flex justify-end">
          <v-btn color="success" @click="openAddTaskDialog">
            ADD NEW TASK
          </v-btn>
        </v-col>
      </v-row>
    </v-col>

    <v-col>
      <v-row dense>
        <template v-if="spansMultipleFacilities">
          <v-col cols="12" md="6">
            <RegionAndFacilitySelect />
          </v-col>
        </template>
        <v-col cols="12" md="3">
          <SelectOrganizationUsers
            v-model="selectedAssigneeId"
            :organization-id="defaultOrganization.id"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="selectedTaskStatus"
            label="Task Status"
            :items="taskStatusList"
            item-text="text"
            item-value="value"
            outlined
            dense
          />
        </v-col>
      </v-row>
      <v-row dense justify="end">
        <v-col cols="12" md="8">
          <div class="d-flex justify-end">
            <div class="d-flex align-center">
              <DateTimeFilter
                v-model="dateRange"
                :time-zone="timeZone"
                :ranges="['Today', '2d', '1w', '1m']"
                :max="maxDate"
                max-range="1m"
                custom
                class="black-border"
              />
              <TaskFilter
                class="ml-2"
                :auto-filter="autoFilter"
                @apply="applyFilter"
              />
            </div>
            <div class="ml-4">
              <SearchField v-model="tableState.search" />
            </div>
          </div>
        </v-col>
      </v-row>
    </v-col>

    <v-skeleton-loader
      :loading="tasksLoading"
      type="table-thead, table-row-divider@10"
    >
      <v-data-table
        :headers="headers"
        :items="tableData"
        :custom-filter="customFilter"
        :custom-sort="customSort"
        :search="tableState.search"
        :sort-by.sync="tableState.sortBy"
        :sort-desc.sync="tableState.sortDesc"
        :page.sync="tableState.page"
        :items-per-page.sync="tableState.itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 20, 50, -1] }"
      >
        <template #item="{ item }">
          <tr>
            <td>
              <ButtonView title="View" @click="openTaskDialog(item)" />
            </td>
            <td style="min-width: 50px">{{ item.taskId }}</td>
            <td style="min-width: 300px">
              <template v-if="spansMultipleFacilities">
                <div class="mt-2">
                  <v-icon color="none" title="Organization" v-bind="$attrs"
                    >mdi-sitemap</v-icon
                  >
                  {{ item.task_location.organization }}
                </div>
                <div class="mt-2">
                  <v-icon color="none" title="Facility" v-bind="$attrs">
                    mdi-domain
                  </v-icon>
                  {{ item.task_location.facility }}
                </div>
                <div class="mt-2" v-if="item.task_location.smart_unit">
                  <v-icon color="none" title="Unit/Zone" v-bind="$attrs">
                    mdi-garage-variant
                  </v-icon>
                  {{ item.task_location.smart_unit }}
                </div>
              </template>
              <template v-else>
                <div v-if="item.task_location.smart_unit">
                  <v-icon color="none" title="Unit/Zone" v-bind="$attrs">
                    mdi-garage-variant
                  </v-icon>
                  {{ item.task_location.smart_unit }}
                </div>
              </template>
            </td>
            <td>{{ item.name }}</td>
            <td>{{ item.type }}</td>
            <td>
              {{
                item.assignee == "SD Automation"
                  ? "Regional Manager"
                  : item.assignee
              }}
            </td>
            <td>
              <div>
                {{ item.status }}
                <span v-if="item.overdue" class="overdue-text">Overdue!</span>
              </div>
            </td>
            <td>
              <SeverityChip :severity="item.priority" />
            </td>
            <td>
              {{ item.due_date }}
            </td>
          </tr>
        </template>
        <template #footer.prepend>
          <ButtonExportCSV
            class="order-last"
            :table-data="tableData"
            :headers="csvHeaders"
            filename="Tasks Report"
          />
          <ButtonPrint
            class="order-last"
            :before-print="showAllItems"
            :after-print="restoreItemsPerPage"
          />
        </template>
      </v-data-table>
    </v-skeleton-loader>
  </div>
</template>

<script>
import RegionAndFacilitySelect from "@components/dashboard/RegionAndFacilitySelect.vue";
import useAccessibleScope from "@components/authentication/useAccessibleScope";
import { ref, computed, onMounted } from "vue";
import SearchField from "@atoms/SearchField.vue";
import { RESOLUTION_STATUS } from "@tod-ui/constants/task_management";
import { TASKS } from "./graphql";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import useDataTable from "@components/common/useDataTable";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import ButtonView from "@atoms/ButtonView.vue";
import { titleCase } from "@tod-ui/helpers/strings";
import { formatDate, formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import SeverityChip from "@tod-ui/components/SeverityChip.vue";
import DialogViewTask from "./DialogViewTask.vue";
import DialogPreInstallModal from "./DialogPreInstallModal.vue";
import SelectOrganizationUsers from "@components/common/SelectOrganizationUsers.vue";
import { formatName } from "./helper";
import { stringSortCollator } from "@tod-ui/helpers/strings";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import { DateTime } from "luxon";
import useDashboard from "@components/dashboard/useDashboard";
import TaskFilter from "./TaskFilter.vue";
import { taskStatuses } from "./helper";
import useFacility from "@components/authentication/useFacility";
import DialogTaskRule from "../settings/DialogTaskRule.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue"; // Add this import

export default {
  name: "PageManageTasks",
  components: {
    TaskFilter,
    RegionAndFacilitySelect,
    SearchField,
    ButtonExportCSV,
    ButtonPrint,
    ButtonView,
    SeverityChip,
    DialogViewTask,
    DialogPreInstallModal,
    SelectOrganizationUsers,
    DateTimeFilter,
    DialogTaskRule,
    AlertsAnimated,
  },
  setup() {
    const { defaultOrganization, isFacilityManager } = useUser();
    const { reachesMultipleFacilities } = useAccessibleScope();
    // The facility filter and the task-location column are relevant whenever the
    // user spans more than one facility — i.e. an org/region admin OR a
    // multiscope facility manager (several facilities in scope). A
    // single-facility manager has nothing to filter, so it stays hidden.
    const spansMultipleFacilities = computed(
      () => !isFacilityManager.value || reachesMultipleFacilities.value
    );
    const { selectedFacility, selectedFacilities } = useDashboard();
    const { currentFacilityId } = useFacility();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
    });
    const { addAlert, addLocalAlert, clearAllAlerts, localAlerts } =
      useAlerts();

    const autoFilter = ref({
      status: "",
      resolutions: [],
      dateRange: { after: null, before: null },
    });
    const isOpenViewTask = ref(false);
    const selectedTaskId = ref({});
    const selectedTask = ref(null);
    const isOpenPreInstallModal = ref(false);
    const selectedAssigneeId = ref(undefined);
    const dateRange = ref({ after: undefined, before: undefined });
    const maxDate = DateTime.now().plus({ years: 1 }).toISO();
    const timeZone = ref(defaultOrganization.timeZone);
    const rawTasksData = ref([]);
    const tasks = ref([]);
    onMounted(() => {
      const searchParams = new URLSearchParams(window.location.search);
      const overdue = searchParams.get("overdue");
      const date = searchParams.get("date");
      const status = searchParams.get("status");

      if (date === "all") {
        dateRange.value = { after: undefined, before: undefined };
      }

      if (overdue === "true" && status === "all") {
        selectedTaskStatus.value = [
          RESOLUTION_STATUS.TODO,
          RESOLUTION_STATUS.PENDING,
        ].map((status) => status.toLowerCase());
        autoFilter.value.resolutions = selectedTaskStatus.value;
        autoFilter.value.status = "overdue";
      } else if (status === "in_progress") {
        selectedTaskStatus.value = RESOLUTION_STATUS.IN_PROGRESS;
        autoFilter.value.status = "";
      } else {
        selectedTaskStatus.value = [
          RESOLUTION_STATUS.TODO,
          RESOLUTION_STATUS.IN_PROGRESS,
          RESOLUTION_STATUS.IN_REVIEW,
          RESOLUTION_STATUS.PENDING,
        ].map((status) => status.toLowerCase());
        autoFilter.value.status = "";
      }
    });

    const taskStatusList = ref([...taskStatuses]);

    const selectedTaskStatus = ref("");
    const tableData = ref([]);
    const headers = ref([
      { text: "View Task", value: "" },
      { text: "Task ID", value: "taskId" },
      { text: "Task Location", value: "task_location", width: "150" },
      { text: "Task Name", value: "name" },
      { text: "Task Type", value: "type" },
      { text: "Assignee", value: "assignee", width: "150" },
      { text: "Task Status", value: "status" },
      { text: "Task Priority", value: "priority" },
      { text: "Task Due Date", value: "due_date", width: "150" },
    ]);

    const csvHeaders = computed(() => [
      { text: "Task ID", value: "taskId" },
      { text: "Organization", value: "organization" },
      { text: "Facility", value: "facility" },
      { text: "Smart Unit / Zone", value: "smart_unit" },
      { text: "Task Name", value: "name" },
      { text: "Task Type", value: "type" },
      { text: "Assignee", value: "assignee" },
      { text: "Task Status", value: "status" },
      { text: "Task Priority", value: "priority" },
      { text: "Task Due Date", value: "due_date" },
    ]);
    // const getFacilityId = computed(() => {
    //   if (selectedFacility.value?.id) {
    //     return selectedFacility.value.id;
    //   }
    //   const pathParts = window.location.pathname.split("/");
    //   return pathParts[2];
    // });

    return {
      defaultOrganization,
      taskStatusList,
      selectedTaskStatus,
      tableData,
      headers,
      csvHeaders,
      selectedFacilities,
      tableState,
      showAllItems,
      restoreItemsPerPage,
      isOpenViewTask,
      selectedTaskId,
      selectedTask,
      isOpenPreInstallModal,
      selectedAssigneeId,
      isFacilityManager,
      spansMultipleFacilities,
      dateRange,
      maxDate,
      timeZone,
      // getFacilityId,
      autoFilter,
      rawTasksData,
      tasks,
      currentFacilityId,
      addAlert,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
    };
  },
  data: () => ({
    filter: {},
    showTaskDialog: false,
  }),
  computed: {
    tasksLoading() {
      const { tasks } = this.$apollo.queries;
      return tasks.loading;
    },
  },
  apollo: {
    tasks: {
      query: TASKS,
      variables() {
        let { after, before } = this.dateRange;
        const url = new URL(window.location.href);
        const searchParams = url.searchParams;
        const status = searchParams.get("status");
        const from = searchParams.get("from");
        if (
          this.selectedTaskStatus === "" &&
          from == "control_hub_dashboard" &&
          status
        ) {
          this.selectedTaskStatus = status;
        }

        if (after && before) {
          const [afterDate, beforeDate] = [after, before].map(DateTime.fromISO);
          if (
            afterDate.day === beforeDate.day &&
            afterDate.month === beforeDate.month &&
            afterDate.year !== beforeDate.year
          ) {
            const today = DateTime.now().startOf("day").toUTC();
            [after, before] = [today.toISO(), today.endOf("day").toISO()];
          }
        }
        const facilityId = this.selectedFacilities.map(
          (facility) => facility.id
        );
        const variables = {
          resolutions: this.selectedTaskStatus,
          assigneeId: this.selectedAssigneeId,
          dateTimeFilter: { after, before },
          ...this.filter,
        };

        if (from === "control_hub_dashboard") {
          const facilities = Array.isArray(this.selectedFacilities)
            ? this.selectedFacilities
            : [];
          if (facilities.length > 1) {
            variables.parentIds = facilities.map((facility) => facility.id);
          } else if (facilities.length === 1) {
            variables.parentId = facilities[0].id;
          } else {
            variables.parentId = this.defaultOrganization.id;
          }
        } else {
          variables.parentIds = facilityId;
        }

        return variables;
      },
      update({ tasks }) {
        const url = new URL(window.location.href);
        const searchParams = url.searchParams;
        const from = searchParams.get("from");

        this.rawTasksData = tasks;

        this.tableData = tasks
          .filter((task) => {
            if (
              from === "control_hub_dashboard" &&
              this.selectedTaskStatus === "overdue"
            ) {
              return (
                task.taskType?.name === "incident_resolution" &&
                task.overdue === true
              );
            } else if (
              this.selectedTaskStatus === "" &&
              from === "control_hub_dashboard"
            ) {
              return task.taskType?.name === "incident_resolution";
            }
            return true;
          })
          .map((task) => ({
            id: task.id,
            taskId: task.taskId,
            task_location: this.spansMultipleFacilities
              ? {
                  organization: task.organization.organization[0]?.name || "",
                  facility: task.organization.facility[0]?.name || "",
                  smart_unit:
                    task.organization.type === "facility"
                      ? ""
                      : task.organization.name,
                }
              : {
                  smart_unit:
                    task.organization.type === "facility"
                      ? ""
                      : task.organization.name,
                },
            organization: task.organization.organization[0]?.name || "",
            facility: task.organization.facility[0]?.name || "",
            smart_unit:
              task.organization.type === "facility"
                ? ""
                : task.organization.name,
            name: task.title,
            type: titleCase(task.taskType.name),
            assignee: formatName(task.assignee),
            status:
              task.resolution === RESOLUTION_STATUS.TODO
                ? "To Do"
                : titleCase(task.resolution),
            priority: titleCase(task.priority),
            due_date:
              task.taskType.name === "incident_resolution"
                ? formatDate(
                    task.dueAt,
                    task.organization?.timeZone ??
                      this.defaultOrganization?.timeZone
                  )
                : formatDateSlashUTC(task.dueAt),
            overdue: task.overdue,
            description: task.description,
            hasChecklist: this.hasChecklist(task),
            originalTask: task,
          }));
      },
      result({ data }) {
        this.rawTasksData = data?.tasks || [];
      },
      // skip() {
      //   return !this.facilityId;
      // },
    },
  },
  watch: {
    rawTasksData: {
      handler(newTasks) {
        this.tasks = newTasks || [];
      },
      immediate: true,
    },
  },
  methods: {
    hasChecklist(task) {
      return (
        task?.actions?.checklist &&
        Array.isArray(task.actions.checklist) &&
        task.actions.checklist.length > 0
      );
    },
    applyFilter(filter) {
      const _filter = { ...filter };

      if (_filter.overdue !== undefined) {
        _filter.isOverdue = _filter.overdue;
        delete _filter.overdue;
      }

      if (_filter.taskStatuses?.length) {
        _filter.resolutions = _filter.taskStatuses;
        delete _filter.taskStatuses;
      }

      if (_filter.locations?.length) {
        _filter.organizationSubtypes = _filter.locations;
        delete _filter.locations;
      }
      if (_filter.location)
        for (let k in _filter) {
          if (_filter[k] === null) {
            delete _filter[k];
          } else if (typeof _filter[k] === "boolean" && !_filter[k]) {
            delete _filter[k];
          } else if (Array.isArray(_filter[k]) && !_filter[k].length) {
            delete _filter[k];
          }
        }

      if (_filter.afterHours) {
        _filter["duringAccessHours"] =
          _filter.afterHours === "after-hours" ? false : undefined;
      }

      this.filter = _filter;
    },
    openTaskDialog(item) {
      const taskTypeName = item.originalTask?.taskType?.name;
      this.isOpenPreInstallModal = taskTypeName === "pre_install_devices";
      this.selectedTaskId = item.id;
      this.selectedTask = item.originalTask || null;
      this.isOpenViewTask = true;
    },
    customFilter(value, search, item) {
      if (!search) return true;
      const _search = search.toLowerCase();
      const match = [
        item.taskId,
        item.task_location.organization.toLowerCase(),
        item.task_location.facility.toLowerCase(),
        item.task_location.smart_unit.toLowerCase(),
        item.name.toLowerCase(),
        item.type.toLowerCase(),
        item.assignee.toLowerCase(),
        item.status.toLowerCase(),
        item.priority.toLowerCase(),
        item.due_date,
      ].join(" ");

      return match.indexOf(_search) >= 0;
    },
    customSort(items, sortBy, sortDesc) {
      const _sortBy = sortBy[0];
      const _sortDesc = sortDesc[0] ? -1 : 1;

      items.sort((a, b) => {
        if (_sortBy === "priority") {
          const priorityOrder = {
            Critical: 4,
            Severe: 3,
            Substantial: 2,
            Moderate: 1,
            Low: 0,
          };

          const priorityA = priorityOrder[a[_sortBy]] || 0;
          const priorityB = priorityOrder[b[_sortBy]] || 0;

          return (priorityB - priorityA) * _sortDesc;
        }

        if (typeof a[_sortBy] === "string") {
          return stringSortCollator.compare(a[_sortBy], b[_sortBy]) * _sortDesc;
        }
        return (b[_sortBy] < a[_sortBy] ? -1 : 1) * _sortDesc;
      });

      return items;
    },
    refetchTasks() {
      this.$apollo.queries.tasks.refetch();
    },
    handleTaskUpdate(updateData) {
      this.refetchTasks();
      this.$emit("task-updated", updateData);
    },
    openAddTaskDialog() {
      this.showTaskDialog = true;
    },
    handleTaskSaved(data) {
      this.refetchTasks();
    },
    handleShowAlert(alert) {
      this.addAlert(alert);
      this.addLocalAlert(alert);
    },
  },
};
</script>

<style scoped>
.overdue-text {
  color: red;
  font-weight: bold;
}
</style>
